/**
 * Family & Sharing — nominate trusted contacts + share a review link.
 * Local-only for MVP (all state in wallet-storage). Backend can pick it up
 * later when a real consumer identity exists.
 */
import { useEffect, useState } from "react";
import { WalletShell, useWalletActive, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { loadWallet, updateWallet } from "@/lib/wallet-storage";
import { Users, Plus, Trash2, Share2, Copy } from "lucide-react";

export default function WalletFamily() {
  const [wallet, setWallet] = useState(null);
  const [name, setName] = useState("");
  const [relation, setRelation] = useState("Spouse");
  const [phone, setPhone] = useState("");
  const active = useWalletActive();

  useEffect(() => { setWallet(loadWallet()); }, []);

  const contacts = wallet?.family || [];

  const addContact = () => {
    if (!name.trim()) return;
    const next = updateWallet({ family: [...contacts, { name: name.trim(), relation, phone, added_at: new Date().toISOString() }] });
    setWallet(next);
    setName(""); setPhone("");
  };

  const removeContact = (i) => {
    const next = updateWallet({ family: contacts.filter((_, idx) => idx !== i) });
    setWallet(next);
  };

  const share = async () => {
    const url = wallet?.last_journey_url || window.location.origin;
    const text = `I've been reviewing a product with Altara. Take a look with me: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Altara Trust Wallet", text, url }); } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(text); alert("Copied — paste in WhatsApp / Signal / SMS"); } catch { /* ignore */ }
    }
  };

  return (
    <WalletShell activeTab={active}>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="wallet-family">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Family & Sharing</div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>Protect the people you care about</h1>
          <p className="text-sm text-slate-600 mt-1">Nominate trusted contacts. When you finalise a big decision they can be notified — with your permission.</p>
        </header>

        {/* Share */}
        <section className="rounded-2xl p-5" style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}>
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4" style={{ color: NAVY }} />
            <div className="text-sm font-semibold" style={{ color: NAVY }}>Share your active review</div>
          </div>
          <p className="text-xs text-slate-700 mt-1">Send a family member the link to review with you. They see the same things you see.</p>
          <button onClick={share} data-testid="family-share" className="mt-3 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium text-white" style={{ background: NAVY }}>
            <Share2 className="w-3.5 h-3.5" /> Share review
          </button>
        </section>

        {/* Contacts */}
        <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4" style={{ color: GOLD }} />
            <div className="text-sm font-semibold" style={{ color: NAVY }}>Trusted contacts · {contacts.length}</div>
          </div>

          {contacts.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No contacts yet. Add someone you trust below.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {contacts.map((c, i) => (
                <div key={i} className="py-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: `${GOLD}22`, color: NAVY }}>
                    {c.name.slice(0, 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{c.name}</div>
                    <div className="text-[11px] text-slate-500">{c.relation}{c.phone ? ` · ${c.phone}` : ""}</div>
                  </div>
                  <button onClick={() => removeContact(i)} className="text-rose-600 hover:text-rose-800" data-testid={`family-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" data-testid="family-name" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-slate-400" />
            <div className="flex gap-2">
              <select value={relation} onChange={e => setRelation(e.target.value)} data-testid="family-relation" className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                {["Spouse", "Partner", "Parent", "Sibling", "Child", "Guardian", "Friend"].map(r => <option key={r}>{r}</option>)}
              </select>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone (optional)" data-testid="family-phone" className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-slate-400" />
            </div>
            <button onClick={addContact} disabled={!name.trim()} data-testid="family-add" className="w-full py-2.5 rounded-xl font-medium text-white text-sm disabled:opacity-40" style={{ background: NAVY }}>
              <Plus className="w-3.5 h-3.5 inline mr-1" /> Add trusted contact
            </button>
          </div>
        </section>
      </div>
    </WalletShell>
  );
}
